import { useEffect, useState } from 'react';

export default function Services() {
  const [selectedFilters, setSelectedFilters] = useState([
    'BUSINESS CONSULTING SERVICES',
    'TRAINING & CAPACITY BUILDING PROGRAMS',
    'PERSONAL & EXECUTIVE COACHING',
    'CORPORATE & INSTITUTIONAL SERVICES'
  ]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const setupObserver = () => {
      // Remove animate-in class from all cards first
      const allCards = document.querySelectorAll('.service-card');
      allCards.forEach((card) => {
        card.classList.remove('animate-in');
        observer.unobserve(card);
      });

      // Then observe all visible cards
      const visibleCards = document.querySelectorAll('.service-card');
      visibleCards.forEach((card) => {
        // Check if card is already in view
        const rect = card.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
        if (isInView) {
          card.classList.add('animate-in');
        } else {
          observer.observe(card);
        }
      });
    };

    const timeoutId = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timeoutId);
      const cards = document.querySelectorAll('.service-card');
      cards.forEach((card) => observer.unobserve(card));
    };
  }, [selectedFilters]);

  const services = [
    {
      section: "BUSINESS CONSULTING SERVICES",
      items: [
        { title: "Business Start-Up Consulting", desc: "Helping entrepreneurs turn their ideas into structured, profitable, and legally registered businesses. From concept to launch — we guide every step.", image: "/images/1.jpeg" },
        { title: "Business Plan & Feasibility Studies", desc: "Professional plans that attract investors and provide clear financial projections for your business idea or expansion.", image: "/images/2.jpeg" },
        { title: "Branding & Marketing Strategy", desc: "Crafting authentic brand identities and marketing strategies that connect emotionally with customers and increase visibility.", image: "/images/3.jpeg" },
        { title: "Customer Service Excellence", desc: "Training and systems to help businesses deliver world-class customer care and build strong client loyalty.", image: "/images/4.jpeg" },
        { title: "Operational Systems & SOP Design", desc: "We design internal systems, workflows, and standard operating procedures that improve efficiency and team performance.", image: "/images/5.jpeg" },
        { title: "SME Growth Consulting", desc: "Strategic guidance for small and medium businesses ready to expand, attract partners, or digitalize operations.", image: "/images/6.jpeg" },
        { title: "Family Business Transformation", desc: "Modernizing traditional family-run companies through governance systems, succession planning, and new leadership models.", image: "/images/7.jpeg" }
      ]
    },
    {
      section: "TRAINING & CAPACITY BUILDING PROGRAMS",
      items: [
        { title: "Leadership & Emotional Intelligence", desc: "Developing self-aware, confident, and emotionally intelligent leaders for the modern workplace.", image: "/images/8.jpeg" },
        { title: "Communication & Conflict Management", desc: "Enhancing professional communication, negotiation, and problem-solving skills to create harmony and productivity.", image: "/images/9.jpeg" },
        { title: "Team Building & Motivation", desc: "Interactive workshops designed to build trust, collaboration, and motivation within teams.", image: "/images/10.jpeg" },
        { title: "Women in Leadership", desc: "Empowering women to lead with confidence, influence, and purpose — both in business and community.", image: "/images/11.jpeg" },
        { title: "Entrepreneurship & Innovation Bootcamp", desc: "Practical training for aspiring entrepreneurs to build, validate, and launch their startups.", image: "/images/13.jpeg" },
        { title: "Sales & Negotiation Mastery", desc: "Equipping sales professionals with tools to build relationships, close deals, and grow revenue ethically.", image: "/images/14.jpeg" },
        { title: "Digital Marketing & Social Media Strategy", desc: "Step-by-step training to build online presence, engage audiences, and grow businesses digitally.", image: "/images/15.jpeg" },
        { title: "Project Management Essentials", desc: "Introducing modern project planning tools and frameworks (PMP, Agile basics) to enhance delivery and accountability.", image: "/images/16.jpeg" },
        { title: "Professional English & Presentation Skills", desc: "Improving communication, confidence, and clarity in business English and public speaking.", image: "/images/17.jpeg" },
        { title: "Public Speaking & Personal Branding", desc: "Developing your voice, message, and confidence to influence any audience or platform.", image: "/images/18.jpeg" }
      ]
    },
    {
      section: "PERSONAL & EXECUTIVE COACHING",
      items: [
        { title: "Executive Coaching", desc: "Confidential 1:1 coaching for leaders seeking clarity, confidence, and peak performance.", image: "/images/19.jpeg" },
        { title: "Career Coaching", desc: "Helping professionals identify their strengths, direction, and next steps for career growth and satisfaction.", image: "/images/20.jpeg" },
        { title: "Life & Confidence Coaching", desc: "Guiding individuals to overcome fear, build self-worth, and align with their authentic potential.", image: "/images/21.jpeg" },
        { title: "Business Coaching for Entrepreneurs", desc: "Practical coaching on strategy, mindset, and structure to help founders scale sustainably.", image: "/images/22.jpeg" }
      ]
    },
    {
      section: "CORPORATE & INSTITUTIONAL SERVICES",
      items: [
        { title: "Organizational Development Programs", desc: "Diagnosing internal challenges and building stronger, more aligned organizational cultures.", image: "/images/23.jpeg" },
        { title: "HR & Talent Management Consulting", desc: "Designing HR frameworks, recruitment systems, and performance management tools for companies.", image: "/images/24.jpeg" },
        { title: "Employee Engagement Surveys", desc: "Measuring and improving workplace satisfaction, motivation, and productivity through data-driven insights.", image: "/images/25.jpeg" },
        { title: "Corporate Training Packages", desc: "Tailored learning programs to strengthen leadership, communication, and technical skills across departments.", image: "/images/26.jpeg" },
        { title: "Monitoring & Evaluation (M&E) Services", desc: "Supporting NGOs and organizations with professional tools to track performance and impact.", image: "/images/27.jpeg" },
        { title: "Strategic Planning Retreats", desc: "Facilitating inspiring retreats that help leaders realign with vision, goals, and company culture.", image: "/images/28.jpeg" }
      ]
    },
    {
      section: "WHITE MOUNTAIN SIGNATURE PROGRAMS",
      items: [
        { title: "The White Mountain Leader", desc: "A 6-week transformation journey that develops confident, emotionally intelligent, and visionary leaders.", image: "/images/29.jpeg" },
        { title: "She Leads", desc: "An empowerment program designed to elevate women into leadership and entrepreneurship with purpose and balance.", image: "/images/30.jpeg" },
        { title: "Next Generation Business", desc: "A hands-on program for young entrepreneurs to turn creative ideas into profitable ventures.", image: "/images/31.jpeg" },
        { title: "The White Mountain Mindset", desc: "A signature coaching system blending psychology, emotional intelligence, and performance tools for personal success.", image: "/images/32.jpeg" },
        { title: "Workplace Wellness & Stress Management", desc: "A holistic program that teaches emotional balance, resilience, and wellness at work.", image: "/images/33.jpeg" }
      ]
    },
    {
      section: "DIGITAL & CREATIVE SOLUTIONS",
      items: [
        { title: "Social Media Strategy & Management", desc: "Helping brands grow online with clear strategy, authentic content, and consistent visibility.", image: "/images/34.jpeg" },
        { title: "Brand Identity & Graphic Design", desc: "Creating professional logos, colors, and brand visuals that communicate trust and style.", image: "/images/35.jpeg" },
        { title: "Website Design & Digital Presence", desc: "Designing clean, user-friendly websites that reflect your business purpose and attract clients.", image: "/images/36.jpeg" },
        { title: "Personal Branding Packages", desc: "Helping professionals build a powerful online image through content, photography, and positioning.", image: "/images/37.jpeg" },
        { title: "Online Course Creation", desc: "Transforming your expertise into income through professionally designed online learning programs.", image: "/images/38.jpeg" }
      ]
    },
    {
      section: "FINANCIAL & INVESTMENT EDUCATION",
      items: [
        { title: "Personal Finance & Wealth Management", desc: "Teaching financial literacy, budgeting, and smart money habits for personal growth.", image: "/images/39.jpeg" },
        { title: "Investment Basics for Entrepreneurs", desc: "Practical introduction to investment opportunities and risk management in local and global markets.", image: "/images/1.jpeg" },
        { title: "Financial Planning for Business Owners", desc: "Helping entrepreneurs understand cash flow, profit, and sustainability for long-term success.", image: "/images/2.jpeg" }
      ]
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="services-hero">
        <img src="/images/frame-6.svg" alt="Frame 6" className="services-hero-decor" />
        <div className="services-hero-content">
          <h1 className="services-hero-title">White Mountain</h1>
          <p className="services-hero-tagline">Empowering People. Elevating Businesses. Inspiring Change.</p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="services-filters">
        <div className="filters-wrapper">
          <h3 className="filters-title">Filter by Category</h3>
          <div className="filter-buttons">
            {services.map((serviceSection, index) => (
              <button
                key={index}
                className={`filter-button ${selectedFilters.includes(serviceSection.section) ? 'active' : ''}`}
                onClick={() => {
                  if (selectedFilters.includes(serviceSection.section)) {
                    setSelectedFilters(selectedFilters.filter(f => f !== serviceSection.section));
                  } else {
                    setSelectedFilters([...selectedFilters, serviceSection.section]);
                  }
                }}
              >
                {serviceSection.section === 'BUSINESS CONSULTING SERVICES' && 'Business Consulting'}
                {serviceSection.section === 'TRAINING & CAPACITY BUILDING PROGRAMS' && 'Training Programs'}
                {serviceSection.section === 'PERSONAL & EXECUTIVE COACHING' && 'Coaching'}
                {serviceSection.section === 'CORPORATE & INSTITUTIONAL SERVICES' && 'Corporate Services'}
                {serviceSection.section === 'WHITE MOUNTAIN SIGNATURE PROGRAMS' && 'Signature Programs'}
                {serviceSection.section === 'DIGITAL & CREATIVE SOLUTIONS' && 'Digital Solutions'}
                {serviceSection.section === 'FINANCIAL & INVESTMENT EDUCATION' && 'Financial Education'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Sections */}
      <section className="services-container">
        {services.filter(serviceSection => selectedFilters.includes(serviceSection.section)).map((serviceSection, sectionIndex) => (
          <div key={`${serviceSection.section}-${sectionIndex}`} className="service-section-wrapper">
            <div className="service-section-header">
              <h2 className="service-section-title">{serviceSection.section}</h2>
              <div className="service-section-divider"></div>
            </div>
            <div className="service-cards-grid">
              {serviceSection.items.map((item, itemIndex) => (
                <div
                  key={`${serviceSection.section}-${itemIndex}-${item.title}`}
                  className="service-card"
                >
                  <div className="service-card-image-wrapper">
                    <img src={item.image} alt={item.title} className="service-card-image" />
                    <div className="service-card-number">{itemIndex + 1}</div>
                  </div>
                  <div className="service-card-content">
                    <h3 className="service-card-title">{item.title}</h3>
                    <p className="service-card-desc">{item.desc}</p>
                  </div>
                  <div className="service-card-hover-effect"></div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* White Mountain Academy Section */}
        <div className="service-section-wrapper academy-section">
          <div className="service-section-header">
            <h2 className="service-section-title">WHITE MOUNTAIN ACADEMY (Future Development)</h2>
            <div className="service-section-divider"></div>
          </div>
          <div className="academy-content">
            <div className="academy-vision">
              <h3>Vision</h3>
              <p>To become Iraq's leading academy for leadership, entrepreneurship, and emotional intelligence.</p>
            </div>
            <div className="academy-offerings">
              <h3>Offerings</h3>
              <ul>
                <li>Certified White Mountain Training Programs</li>
                <li>Online Courses & Hybrid Learning</li>
                <li>Internships & Mentorships</li>
                <li>Networking Events & Annual White Mountain Summit</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
